/**
 * DateChip — React (web) implementation.
 *
 * All styling references the shared constants/resolvers in DateChip.ts.
 * Exposes a forwarded ref + tabIndex so WeekStrip can drive roving tabindex
 * focus management (ADR-025). No raw hex values or pixel literals here.
 */

import React, { useId, useState } from 'react';
import { colors } from '../../elements/colors';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { labelColorsFor } from '../Label/Label';
import { Icon } from '../Icon/Icon.web';
import type { DateChipState, DateChipMarker, DateChipTone } from './DateChip';
import {
  dateChipColorsFor,
  dateChipMarkerFor,
  dateChipAccessibleLabelFor,
  DATE_CHIP_NUMBER_TYPOGRAPHY,
  DATE_CHIP_WEEKDAY_TYPOGRAPHY,
  DATE_CHIP_RADIUS,
  DATE_CHIP_MARKER_GAP,
  dateChipPaddingFor,
  dateChipMinWidthFor,
  dateChipLabelsGapFor,
  DATE_CHIP_BADGE_TYPOGRAPHY,
  DATE_CHIP_BADGE_HEIGHT,
  DATE_CHIP_BADGE_RADIUS,
  DATE_CHIP_BADGE_INSET_HORIZONTAL,
  DATE_CHIP_MARKER_SIZE,
  DATE_CHIP_MARKER_RADIUS,
} from './DateChip';

// Web-only looks. Hover previews an unselected default-tone chip; Disabled
// marks a day with nothing to book (native only greys the text).
const WEB_DATE_CHIP_HOVER_SURFACE = colors.background.secondary;
const WEB_DATE_CHIP_HOVER_BORDER = colors.stroke.primary;
const WEB_DATE_CHIP_BORDER_WIDTH = sizing.stroke.hairline;
const WEB_DATE_CHIP_DISABLED_SURFACE = colors.background.container;
const WEB_DATE_CHIP_DISABLED_BORDER = colors.stroke.tertiary;
const WEB_DATE_CHIP_DISABLED_TEXT = colors.text.tertiary;
const WEB_DATE_CHIP_DISABLED_OPACITY = states.opacity.disabled;

// Web-only `picker` look (v7 Web/Date chip): weekday over the number, a
// near-black fill when selected and a hairline input border when not. It
// fills whatever width its container gives it; the strip owns the layout.
const WEB_PICKER_SELECTED_SURFACE = colors.button.primary;
const WEB_PICKER_SELECTED_TEXT = colors.text.inverted;
const WEB_PICKER_SURFACE = colors.background.container;
const WEB_PICKER_BORDER = colors.stroke.input;
const WEB_PICKER_WEEKDAY_TEXT = colors.text.secondary;
const WEB_PICKER_NUMBER_TEXT = colors.text.primary;
const WEB_PICKER_PADDING_VERTICAL = spacing.sm;
const WEB_PICKER_LABELS_GAP = spacing.xxs;

/** The web date chip's looks: the app's week strip, or the form's day picker. */
export type WebDateChipVariant = 'strip' | 'picker';

const DATE_CHIP_FOCUS_STYLE_ID = 'galapagos-date-chip-focus-visible';
const DATE_CHIP_FOCUS_SELECTOR = '[data-galapagos-date-chip="true"]';

export interface WebDateChipProps {
  actionId?: string;
  dayLabel: string;
  weekdayLabel: string;
  state?: DateChipState;
  today?: boolean;
  marker?: DateChipMarker;
  tone?: DateChipTone;
  /** Announced meaning of the marker glyph — caller-owned copy (ADR-017). */
  markerAccessibilityLabel?: string;
  /** Announced meaning of the today treatment — caller-owned copy (ADR-017). */
  todayAccessibilityLabel?: string;
  /** What activating the chip does — caller-owned copy (ADR-017). */
  accessibilityHint?: string;
  badgeLabel?: string;
  /**
   * When true the chip is inert and exposed as a disabled radio (`aria-disabled`);
   * press is suppressed. It stays focusable so the roving tab stop and arrow-key
   * exploration still reach it. It renders faded with a dashed border and a
   * struck-through date.
   */
  disabled?: boolean;
  onPress?: () => void;
  tabIndex?: number;
  'data-testid'?: string;
  accessibilityLabel?: string;
  presentation?: boolean;
  /**
   * Web only. `strip` (default) is the app's week strip chip; `picker` is the
   * web form's day picker (weekday on top, bordered, filling its container).
   */
  variant?: WebDateChipVariant;
}

export const DateChip = React.forwardRef<HTMLElement, WebDateChipProps>(function DateChip(
  {
    actionId = 'ui.date-chip.activate',
    dayLabel,
    weekdayLabel,
    state = 'default',
    today,
    marker,
    tone = 'default',
    markerAccessibilityLabel,
    todayAccessibilityLabel,
    accessibilityHint,
    badgeLabel,
    disabled = false,
    onPress,
    tabIndex,
    'data-testid': testId,
    accessibilityLabel,
    presentation = false,
    variant = 'strip',
  },
  ref,
) {
  const picker = variant === 'picker';
  ensureFocusVisibleStyle(DATE_CHIP_FOCUS_STYLE_ID, [DATE_CHIP_FOCUS_SELECTOR]);
  const [hovered, setHovered] = useState(false);
  const stripColors = dateChipColorsFor(state, today, tone);
  const baseColors = picker
    ? state === 'selected'
      ? {
          surface: WEB_PICKER_SELECTED_SURFACE,
          text: WEB_PICKER_SELECTED_TEXT,
          borderColor: WEB_PICKER_SELECTED_SURFACE,
          borderWidth: 0,
        }
      : {
          surface: WEB_PICKER_SURFACE,
          text: WEB_PICKER_NUMBER_TEXT,
          borderColor: WEB_PICKER_BORDER,
          borderWidth: WEB_DATE_CHIP_BORDER_WIDTH,
        }
    : stripColors;
  const hoverable =
    !disabled && !presentation && state !== 'selected' && tone === 'default' && !!onPress;
  const dateColors = disabled
    ? {
        surface: WEB_DATE_CHIP_DISABLED_SURFACE,
        text: WEB_DATE_CHIP_DISABLED_TEXT,
        borderColor: WEB_DATE_CHIP_DISABLED_BORDER,
        borderWidth: WEB_DATE_CHIP_BORDER_WIDTH,
      }
    : hoverable && hovered
      ? {
          ...baseColors,
          surface: WEB_DATE_CHIP_HOVER_SURFACE,
          borderColor: WEB_DATE_CHIP_HOVER_BORDER,
          borderWidth: WEB_DATE_CHIP_BORDER_WIDTH,
        }
      : baseColors;
  // The hover and disabled border must not resize the chip, so the padding
  // gives back whatever border those states add.
  const addedBorder = Math.max(0, dateColors.borderWidth - baseColors.borderWidth);
  const inset = (padding: number) => Math.max(0, padding - addedBorder);
  const markerDesc = dateChipMarkerFor(marker, state, today);
  const handlePress = disabled || presentation ? undefined : onPress;
  const a11yLabel =
    accessibilityLabel ??
    dateChipAccessibleLabelFor({
      weekdayLabel,
      dayLabel,
      state,
      today,
      marker,
      markerAccessibilityLabel,
      todayAccessibilityLabel,
      badgeLabel,
    });

  // The hint goes through `aria-describedby` pointing at a visually-hidden node
  // rather than `aria-description`, which is an ARIA 1.3 draft attribute with
  // effectively no screen-reader support — setting it would look like parity
  // with native without being it. Mirrors `PressableSurface` and `CounterPill`.
  // `useId` rather than deriving from the testID: two hint-bearing chips
  // rendered without a testID would otherwise emit the same DOM id, and
  // `aria-describedby` resolves to the first, so the second chip would announce
  // the wrong hint.
  const generatedId = useId();
  const hintId = accessibilityHint ? `${testId ?? 'date-chip'}-hint-${generatedId}` : undefined;
  const setRef = (node: HTMLElement | null) => {
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const badged = Boolean(badgeLabel);
  const stripPadding = dateChipPaddingFor(state, badged);
  // The picker keeps one size whether selected or not: its padding gives back
  // the border an unselected chip draws.
  const padding = picker
    ? {
        top: WEB_PICKER_PADDING_VERTICAL,
        bottom: WEB_PICKER_PADDING_VERTICAL,
        horizontal: stripPadding.horizontal,
      }
    : stripPadding;
  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: DATE_CHIP_MARKER_GAP,
    // minWidth (not a hard width) so a badged chip still grows to fit a long
    // badge on one line; the day column stays uniform for the rest.
    minWidth: dateChipMinWidthFor(badged),
    width: picker ? '100%' : undefined,
    boxSizing: picker ? 'border-box' : undefined,
    paddingTop: inset(padding.top) - (picker ? baseColors.borderWidth : 0),
    paddingBottom: inset(padding.bottom) - (picker ? baseColors.borderWidth : 0),
    paddingLeft: inset(padding.horizontal),
    paddingRight: inset(padding.horizontal),
    borderRadius: DATE_CHIP_RADIUS,
    borderStyle: disabled ? 'dashed' : 'solid',
    borderColor: dateColors.borderColor,
    borderWidth: dateColors.borderWidth,
    backgroundColor: dateColors.surface,
    cursor: disabled ? 'not-allowed' : handlePress ? 'pointer' : 'default',
    opacity: disabled ? WEB_DATE_CHIP_DISABLED_OPACITY : undefined,
    userSelect: 'none',
  };

  const numberStyle: React.CSSProperties = {
    fontFamily: DATE_CHIP_NUMBER_TYPOGRAPHY.fontFamily,
    fontWeight: DATE_CHIP_NUMBER_TYPOGRAPHY.fontWeight,
    fontSize: DATE_CHIP_NUMBER_TYPOGRAPHY.fontSize,
    lineHeight: `${DATE_CHIP_NUMBER_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: DATE_CHIP_NUMBER_TYPOGRAPHY.letterSpacing,
    color: dateColors.text,
    textDecoration: disabled ? 'line-through' : undefined,
  };

  const weekdayStyle: React.CSSProperties = {
    fontFamily: DATE_CHIP_WEEKDAY_TYPOGRAPHY.fontFamily,
    fontWeight: DATE_CHIP_WEEKDAY_TYPOGRAPHY.fontWeight,
    fontSize: DATE_CHIP_WEEKDAY_TYPOGRAPHY.fontSize,
    lineHeight: `${DATE_CHIP_WEEKDAY_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: DATE_CHIP_WEEKDAY_TYPOGRAPHY.letterSpacing,
    color: picker && state !== 'selected' && !disabled ? WEB_PICKER_WEEKDAY_TEXT : dateColors.text,
  };

  // The badged chip's compact badge: one caption line, no vertical padding.
  const badgeColors = labelColorsFor();
  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: DATE_CHIP_BADGE_HEIGHT,
    paddingLeft: DATE_CHIP_BADGE_INSET_HORIZONTAL,
    paddingRight: DATE_CHIP_BADGE_INSET_HORIZONTAL,
    borderRadius: DATE_CHIP_BADGE_RADIUS,
    backgroundColor: badgeColors.background,
    color: badgeColors.text,
    fontFamily: DATE_CHIP_BADGE_TYPOGRAPHY.fontFamily,
    fontWeight: DATE_CHIP_BADGE_TYPOGRAPHY.fontWeight,
    fontSize: DATE_CHIP_BADGE_TYPOGRAPHY.fontSize,
    lineHeight: `${DATE_CHIP_BADGE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: DATE_CHIP_BADGE_TYPOGRAPHY.letterSpacing,
    whiteSpace: 'nowrap',
  };

  const dotStyle: React.CSSProperties = markerDesc
    ? {
        width: DATE_CHIP_MARKER_SIZE,
        height: DATE_CHIP_MARKER_SIZE,
        // Intrinsic size floor so the marker dot stays round in a flex row.
        minWidth: DATE_CHIP_MARKER_SIZE,
        minHeight: DATE_CHIP_MARKER_SIZE,
        borderRadius: DATE_CHIP_MARKER_RADIUS,
        backgroundColor: markerDesc.color,
      }
    : {};

  const content = (
    <>
      {/* The picker has no marker row: a day to book carries no streak or dot. */}
      {picker && !markerDesc ? null : (
        <span
          aria-hidden
          style={{
            height: DATE_CHIP_MARKER_SIZE,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {markerDesc?.glyph === 'check' ? (
            <Icon name="tick" size={DATE_CHIP_MARKER_SIZE} color={markerDesc.color} aria-hidden />
          ) : markerDesc ? (
            <span style={dotStyle} />
          ) : null}
        </span>
      )}
      <span
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: picker ? WEB_PICKER_LABELS_GAP : dateChipLabelsGapFor(badged),
        }}
      >
        {picker && !badgeLabel ? <span style={weekdayStyle}>{weekdayLabel}</span> : null}
        <span style={numberStyle}>{dayLabel}</span>
        {badgeLabel ? (
          <span style={badgeStyle}>{badgeLabel}</span>
        ) : picker ? null : (
          <span style={weekdayStyle}>{weekdayLabel}</span>
        )}
      </span>
      {hintId ? (
        <span
          id={hintId}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {accessibilityHint}
        </span>
      ) : null}
    </>
  );

  if (presentation) {
    return (
      <div
        ref={setRef}
        role="listitem"
        style={buttonStyle}
        aria-label={a11yLabel}
        aria-describedby={hintId}
        data-testid={testId}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      data-analytics-action={actionId}
      ref={setRef}
      type="button"
      role="radio"
      style={buttonStyle}
      tabIndex={tabIndex}
      onClick={handlePress}
      onPointerEnter={(event) => {
        // Mouse only: a tap fires a pointer-enter with no matching leave.
        if (event.pointerType === 'mouse') setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      aria-checked={state === 'selected'}
      aria-disabled={disabled || undefined}
      aria-label={a11yLabel}
      aria-describedby={hintId}
      data-galapagos-date-chip="true"
      data-testid={testId}
    >
      {content}
    </button>
  );
});
